/**
 * Define how Tab behaves at the end of a populated block, inserting the next
 * related block type.
 */

import { type NodeType, type ResolvedPos } from 'prosemirror-model'
import { type EditorState, Plugin, TextSelection } from 'prosemirror-state'

import { ScriptFormatMap } from '@showrunner/codex'
import { schema } from '@showrunner/prose-schemas'

import { isEmptyBlock } from '#util/prosemirrorHelpers/wrapped-block-helpers'

import { isAtLogicalEnd } from '../selection'

/** A block to insert, with the cursor's offset into its text. */
type NextBlock = { type: NodeType; text: string; cursor: number }

/**
 * Insert the next screenplay block on Tab, using its schema placeholder.
 */
export function createNextBlockPlugin() {
  return new Plugin({
    props: {
      handleKeyDown(view, event) {
        const { key, shiftKey, altKey, ctrlKey, metaKey } = event
        if (key !== 'Tab' || shiftKey || altKey || ctrlKey || metaKey) {
          return false
        }

        const { state } = view
        if (!isScreenplay(state)) return false

        const { selection } = state
        if (!isAtLogicalEnd(selection)) return false

        const { $from } = selection
        const block = $from.parent
        if (isEmptyBlock(block) || isDualDialogue($from)) return false

        const nextBlock = getNextTabBlock(block.type)
        if (!nextBlock) return false

        const { type, text, cursor } = nextBlock
        const insertAt = $from.after()
        const tr = state.tr.insert(
          insertAt,
          type.create(null, text ? type.schema.text(text) : null),
        )
        tr.setSelection(TextSelection.create(tr.doc, insertAt + 1 + cursor))
        view.dispatch(tr.scrollIntoView())
        return true
      },
    },
  })
}

/** Return the next block in a sequence, given an existing block type. */
function getNextTabBlock(blockType: NodeType): NextBlock | undefined {
  switch (blockType) {
    case schema.nodes.action:
    case schema.nodes.sceneHeading:
      return createNextBlock(schema.nodes.action)
    case schema.nodes.general:
      return createNextBlock(schema.nodes.general)
    case schema.nodes.character:
    case schema.nodes.dialogue:
      return createNextBlock(schema.nodes.parenthetical)
    case schema.nodes.parenthetical:
      return createNextBlock(schema.nodes.dialogue)
    case schema.nodes.transition:
    case schema.nodes.new_act:
      return createNextBlock(schema.nodes.sceneHeading)
    case schema.nodes.end_of_act:
      return createNextBlock(schema.nodes.new_act)
  }
}

function createNextBlock(type: NodeType): NextBlock {
  const { placeholder } = type.spec
  if (!placeholder) return { type, text: '', cursor: 0 }

  return {
    type,
    text: placeholder.prefix + placeholder.suffix,
    cursor: placeholder.prefix.length,
  }
}

function isScreenplay(state: EditorState): boolean {
  return state.doc.attrs.docType === ScriptFormatMap.SCREENPLAY
}

function isDualDialogue($pos: ResolvedPos): boolean {
  for (let depth = $pos.depth; depth > 0; depth--) {
    if ($pos.node(depth).type === schema.nodes.dual_dialogue) return true
  }
  return false
}
